/**
 * Profile completion for user_role_fields: backend may set is_filled true while value is empty or a placeholder.
 */

export type UserRoleFieldRow = {
  value?: unknown;
  is_filled?: boolean;
  role_field?: {
    is_required?: boolean;
    is_deleted?: boolean;
    is_publish?: boolean;
    field?: {
      type?: string;
      name?: string;
    };
  };
};

function isUnverifiedPlaceholder(field: UserRoleFieldRow, trimmed: string): boolean {
  if (trimmed.toLowerCase() !== 'unverified') return false;
  const t = (field?.role_field?.field?.type || '').toLowerCase();
  return t === 'alphanum' || t === 'text';
}

export function isUserRoleFieldValueProvided(field: UserRoleFieldRow): boolean {
  const v = field?.value;
  if (v === null || v === undefined) return false;
  if (typeof v === 'string') {
    const t = v.trim();
    if (t === '') return false;
    if (isUnverifiedPlaceholder(field, t)) return false;
    return true;
  }
  if (typeof v === 'number') return Number.isFinite(v);
  if (typeof v === 'boolean') return true;
  if (v instanceof Date) return !Number.isNaN(v.getTime());
  if (typeof v === 'object' && v !== null) {
    return Object.prototype.toString.call(v) !== '[object Object]' || Object.keys(v).length > 0;
  }
  return false;
}

export function isUserRoleFieldComplete(field: UserRoleFieldRow): boolean {
  return field?.is_filled === true && isUserRoleFieldValueProvided(field);
}

function getMandatoryFields(fields: readonly UserRoleFieldRow[]): UserRoleFieldRow[] {
  return fields.filter(f =>
    f?.role_field?.is_required === true &&
    f?.role_field?.is_deleted !== true &&
    f?.role_field?.is_publish !== false
  );
}

function countCompleteFields(fields: readonly UserRoleFieldRow[]): number {
  let n = 0;
  for (const field of fields) {
    if (isUserRoleFieldComplete(field)) n++;
  }
  return n;
}

export function computeProfileCompletionPercent(
  fields: UserRoleFieldRow[] | undefined | null
): number {
  if (!Array.isArray(fields) || fields.length === 0) {
    return 100;
  }
  const mandatory = getMandatoryFields(fields);
  if (mandatory.length === 0) return 100;
  const completed = countCompleteFields(mandatory);
  return Math.round((completed / mandatory.length) * 100);
}

/** Parent rows from getPlayerInfo `parents` (or form snapshot) for completion checks */
export type PlayerParentRow = {
  parent_role_name?: string;
  /** API may send number or string */
  parent_user_role_id?: number | string;
  parent_role_field_value?: string;
};

/** Metadata from get_role_parents API */
export type RoleParentMeta = {
  parent_role_name?: string;
  parent_user_roles?: unknown[] | null;
};

const NAME_ADMIN = 'Admin';
const NAME_MA = 'Member Association';
const NAME_CLUB = 'Club';

export function parentRowHasSelectedValue(p: PlayerParentRow | undefined | null): boolean {
  if (!p) return false;
  const id = p.parent_user_role_id;
  if (id != null && id !== '') {
    const n = typeof id === 'number' ? id : Number(String(id).trim());
    if (Number.isFinite(n) && n > 0) return true;
  }
  const v = p.parent_role_field_value;
  return v != null && String(v).trim() !== '';
}

function scanParentMeta(meta: readonly RoleParentMeta[]): { adminNeeded: boolean; maClubNeeded: boolean } {
  let adminNeeded = false;
  let maClubNeeded = false;
  for (const p of meta) {
    if ((p.parent_user_roles?.length ?? 0) === 0) continue;
    const name = p.parent_role_name;
    if (name === NAME_ADMIN) adminNeeded = true;
    else if (name === NAME_MA || name === NAME_CLUB) maClubNeeded = true;
    if (adminNeeded && maClubNeeded) break;
  }
  return { adminNeeded, maClubNeeded };
}

function normalizeToArray<T>(value: T[] | undefined | null): T[] {
  return Array.isArray(value) ? value : [];
}

function countExtraParentTotal(adminNeeded: boolean, maClubNeeded: boolean): number {
  return (adminNeeded ? 1 : 0) + (maClubNeeded ? 1 : 0);
}

function parentSlotsSatisfied(
  adminNeeded: boolean,
  maClubNeeded: boolean,
  adminFound: boolean,
  maClubFound: boolean,
): boolean {
  return (!adminNeeded || adminFound) && (!maClubNeeded || maClubFound);
}

function scanParentSelections(
  parList: readonly PlayerParentRow[],
  adminNeeded: boolean,
  maClubNeeded: boolean,
): { adminFound: boolean; maClubFound: boolean } {
  let adminFound = false;
  let maClubFound = false;
  for (const p of parList) {
    if (!parentRowHasSelectedValue(p)) {
      continue;
    }
    const name = p.parent_role_name;
    if (name === NAME_ADMIN) {
      adminFound = true;
    } else if (name === NAME_MA || name === NAME_CLUB) {
      maClubFound = true;
    }
    if (parentSlotsSatisfied(adminNeeded, maClubNeeded, adminFound, maClubFound)) {
      break;
    }
  }
  return { adminFound, maClubFound };
}

function countCompletedParentSlots(
  adminNeeded: boolean,
  maClubNeeded: boolean,
  parents: readonly PlayerParentRow[],
): number {
  const extraTotal = countExtraParentTotal(adminNeeded, maClubNeeded);
  if (extraTotal === 0) {
    return 0;
  }
  const { adminFound, maClubFound } = scanParentSelections(parents, adminNeeded, maClubNeeded);
  let extraDone = 0;
  if (adminNeeded && adminFound) {
    extraDone++;
  }
  if (maClubNeeded && maClubFound) {
    extraDone++;
  }
  return extraDone;
}

/**
 * All role fields plus two parent slots when applicable: Admin, and Member Association or Club (at least one).
 */
export function computeDashboardProfileCompletionPercent(
  fields: UserRoleFieldRow[] | undefined | null,
  roleParentsMeta: RoleParentMeta[] | undefined | null,
  parents: PlayerParentRow[] | undefined | null
): number {
  const fieldList = getMandatoryFields(normalizeToArray(fields));
  const completedFields = countCompleteFields(fieldList);

  const { adminNeeded, maClubNeeded } = scanParentMeta(normalizeToArray(roleParentsMeta));
  const extraTotal = countExtraParentTotal(adminNeeded, maClubNeeded);
  const extraDone = countCompletedParentSlots(
    adminNeeded,
    maClubNeeded,
    normalizeToArray(parents),
  );

  const totalUnits = fieldList.length + extraTotal;
  if (totalUnits === 0) {
    return 100;
  }
  return Math.round(((completedFields + extraDone) / totalUnits) * 100);
}

export function persistProfileCompletionLocalStorage(percent: number): void {
  if (!globalThis.window?.localStorage) return;
  localStorage.setItem('profile_completion_percent', String(percent));
  localStorage.setItem('is_profile_completed', percent >= 100 ? 'true' : 'false');
}
